/**
 * TODO:
 * - fix @ts-ignore
 */
import moment from 'moment';

import {
  IOrderTransactionEvent,
  IPartyTransactionEvent,
} from '../../types/Events';
import { IIPENNotaryProfile } from '../../types/Notary';
import { IOrder } from '../../types/Order';
import { ISigner } from '../../types/Parties';
import { IPhoneNumber } from '../../types/PhoneNumber';
import { OrderStatus } from '../constants/OrderStatus';
import { black, primaryBlue, successGreen } from '../constants/colors';
import { IPartyName, ISearchParams } from '../type';
import { Status } from './notaryStatus';

export const parseFilterParams = (string: string) => {
  if (string) {
    const filters = string.split(';');

    const array = [];
    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i].split(',');
      const obj = { filter: filter[0], id: filter[1], value: filter[2] };
      if (filter[3]) {
        // @ts-ignore
        obj.value = [filter[2], filter[3]];
      }
      array.push(obj);
    }
    return array;
  } else {
    return [];
  }
};

/**
 * formats UTC date and time into format param in local time
 * @param data Date in UTC
 * @param format Date and time format. See https://momentjs.com/docs/#/displaying/
 * @param timeZone the timezone to display it in
 */
export const formatDate = (
  data?: any | null,
  format?: any,
  timeZone?: any
): string => {
  if (data && timeZone && format) {
    return moment.tz(data, timeZone).format(format);
  } else if (data && format) {
    return moment.utc(data).local().format(format);
  } else if (data && timeZone) {
    return moment.tz(data, timeZone).format();
  } else {
    return moment(data).isValid() ? moment.utc(data).local().format() : data;
  }
};

/**
 * For RON orders, Start time needs to be in the future + 10 mins if today is chosen
 * @param date string in orderDetail
 * @param timeZone string
 */
export const formatRonStartingDate = (date?: any, timeZone?: any): string => {
  return moment().to(moment(date)).includes('ago')
    ? moment().add(10, 'minutes').format()
    : moment
        .tz(moment(date).format('YYYY-MM-DD'), timeZone)
        .startOf('day')
        .format();
};
/**
 * For RON orders, End time needs to be maximized to the end of day
 * @param date string in orderDetail
 * @param timeZone string
 */
export const formatRonEndingDate = (date?: any, timeZone?: any): string => {
  return moment
    .tz(moment(date).format('YYYY-MM-DD'), timeZone)
    .endOf('day')
    .format();
};

export const address = (data: any): string => {
  const properties =
    data?.OrderOverview?.Properties ?? data?.OrderDetails?.Properties;
  let address = '';
  if (
    properties &&
    properties[0] &&
    properties[0].Addresses &&
    properties[0].Addresses[0]
  ) {
    address += properties[0].Addresses[0].Line1
      ? properties[0].Addresses[0].Line1
      : '';
    address += properties[0].Addresses[0].Line2
      ? ' ' + properties[0].Addresses[0].Line2
      : '';
    address += properties[0].Addresses[0].Line3
      ? ' ' + properties[0].Addresses[0].Line3
      : '';
    address += properties[0].Addresses[0].City
      ? ', ' + properties[0].Addresses[0].City
      : '';
    address += properties[0].Addresses[0].State?.Code
      ? ' ' + properties[0].Addresses[0].State.Code
      : '';
    address += properties[0].Addresses[0].ZipCode
      ? ' ' + properties[0].Addresses[0].ZipCode
      : '';
  }
  return address;
};

export const a11yProps: any = (index: number) => {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
};

export const lender = (data: any): string => {
  const loan = data?.OrderDetails?.NewLoans ?? data?.OrderOverview?.NewLoans;
  let lender = '';
  if (loan) {
    lender += loan[0]?.Lender?.Name ? loan[0].Lender.Name : '';
  }
  return lender;
};

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const customer = (data: any): string => {
  const signer = data?.OrderOverview?.Signers ?? data?.OrderDetails?.Signers;
  if (signer && signer.length > 0) {
    const nameObj: IPartyName = {
      FirstName: signer[0]?.FirstName,
      LastName: signer[0]?.LastName,
      MiddleName: signer[0]?.MiddleName,
      Suffix: signer[0]?.Suffix,
    };
    return formatPartyName(nameObj, 'signer');
  }
  return '';
};

export const transactionType = (data: any): string => {
  let loanType = '';
  if (data?.TransactionType) {
    loanType += data?.TransactionType ? data?.TransactionType : '';
  }
  return loanType;
};

/**
 * compare date
 * @returns boolean
 * @param d1
 */
export const isDateToday = (d1: any): boolean => {
  const d2 = new Date();
  return (
    moment(d1).isSame(d2, 'year') &&
    moment(d1).isSame(d2, 'month') &&
    moment(d1).isSame(d2, 'day')
  );
};

/**
 *
 * @param expiryDate Notary profile expiring date
 * @returns number | null
 */
export const calcDaysUntilExpiry = (expiryDate: any | null) => {
  let expiry = null;
  if (expiryDate) {
    const expiryDay = moment(new Date(expiryDate));
    expiry = moment(expiryDay)
      .startOf('day')
      .diff(moment(Date.now()).startOf('day'), 'days');
  }
  return expiry;
};

/**
 *
 * @param value unformatted phone number, e.g. 5125555555
 * @returns phone number format, e.g. ' (512) 555-5555' - space in front for international code
 *
 */
export const normalizePhoneInput = (value: string): string => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) return value;

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, '');

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  // we need to return the value with no formatting if its less than four digits
  // this is to avoid weird behavior that occurs if you  format the area code too early
  if (phoneNumberLength < 4) return phoneNumber;

  // if phoneNumberLength is greater than 4 and less the 7 we start to return
  // the formatted number
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  // finally, if the phoneNumberLength is greater then seven, we add the last
  // bit of formatting and return it.
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

/**
 *
 * @param value unformatted phone number, e.g. 5125555555
 * @returns { object } see type def for IPhoneNumber
 */
export const constructPhoneObject = (value?: string): IPhoneNumber => {
  const phoneObj: IPhoneNumber = {
    CountryCode: '1',
    AreaCode: '',
    Number: '',
    Extension: null,
    Type: 'Cellular',
  };

  if (value) {
    const onlyNums = value.replace(/[^\d]/g, ''); // only allows 0-9
    const areaCodeString = onlyNums.substring(0, 3);
    const numberString = onlyNums.substring(3, 10);

    if (areaCodeString && areaCodeString.length === 3) {
      phoneObj.AreaCode = areaCodeString;
    }
    if (numberString && numberString.length === 7) {
      phoneObj.Number = numberString;
    }
  }
  return phoneObj;
};

/**
 *
 * @param obj phone number object (see IPhoneNumber)
 * @returns common phone number format and country code, e.g. '1 (512) 555-5555'
 */
export const phoneObjToString = (obj: IPhoneNumber): string => {
  let phoneNumber = '';
  phoneNumber +=
    obj?.CountryCode && obj?.CountryCode.length > 0 ? obj.CountryCode : '';
  phoneNumber +=
    obj?.AreaCode && obj?.AreaCode.length > 0 ? ` (${obj.AreaCode})` : '';
  phoneNumber +=
    obj?.Number && obj?.Number.length > 0
      ? ` ${obj.Number.substring(0, 3) + '-' + obj.Number.substring(3, 8)}`
      : '';
  return phoneNumber;
};

/**
 *
 * @param value unformatted social security number, e.g. 123456789
 * @returns ssn format: xxx - xx - xxxx, e.g. 123 - 45 - 6789
 */
export const formatSSN = (value: string): string => {
  if (!value) return value;

  const onlyNums = value.replace(/[^\d]/g, ''); // only allows 0-9

  if (onlyNums.length <= 3) return onlyNums;
  if (onlyNums.length <= 5)
    return `${onlyNums.slice(0, 3)} - ${onlyNums.slice(3)}`;
  if (onlyNums.length <= 6)
    return `${onlyNums.slice(0, 3)} - ${onlyNums.slice(
      3,
      5
    )} - ${onlyNums.slice(5)}`;
  if (onlyNums.length <= 9)
    return `${onlyNums.slice(0, 3)} - ${onlyNums.slice(
      3,
      5
    )} - ${onlyNums.slice(5, 9)}`;

  return `${onlyNums.slice(0, 3)} - ${onlyNums.slice(3, 5)} - ${onlyNums.slice(
    5,
    9
  )}`;
};

export const mergeEvents = (order: IOrder) => {
  const newOrderObj = order;
  const mergedEvents = newOrderObj.Events;
  const signers = newOrderObj?.OrderDetails?.Signers;

  // Format Transaction Events
  if (mergedEvents) {
    for (let i = 0; i < mergedEvents.length; i++) {
      switch (mergedEvents[i].Name) {
        case OrderStatus.OpenLinksSent:
          mergedEvents[i].Name = 'Documents Distributed';
          break;
        case OrderStatus.OpenAllPreviewed:
          mergedEvents[i].Name = 'All Reviewed';
          break;
        case OrderStatus.OpenAllSigned:
          mergedEvents[i].Name = 'All Signed';
          break;
        default:
          break;
      }
    }
  }

  // Format Signer Events
  if (signers && signers.length) {
    for (let i = 0; i < signers.length; i++) {
      if (
        signers[i].SolexSigner &&
        // @ts-ignore
        signers[i].SolexSigner.SignerEvents &&
        // @ts-ignore
        signers[i].SolexSigner.SignerEvents.length
      ) {
        for (
          let i2 = 0;
          // @ts-ignore
          i2 < signers[i].SolexSigner?.SignerEvents.length;
          i2++
        ) {
          if (
            !signers[i]?.SolexSigner?.SignerEvents[i2]?.Name?.includes(
              OrderStatus.OpenLinksSent
            )
          ) {
            const nameObj: IPartyName = {
              FirstName: signers[i]?.FirstName,
              LastName: signers[i]?.LastName,
              MiddleName: signers[i]?.MiddleName,
              Suffix: signers[i]?.Suffix,
            };
            const formattedSignerName = formatPartyName(nameObj, 'signer');
            mergedEvents?.push({
              ...signers[i]?.SolexSigner?.SignerEvents[i2],
              Signer: formattedSignerName,
            });
          }
        }
      }
    }
  }

  function compare(
    a: IPartyTransactionEvent | IOrderTransactionEvent,
    b: IPartyTransactionEvent | IOrderTransactionEvent
  ) {
    if (a.TimeOccured && b.TimeOccured) {
      if (a.TimeOccured < b.TimeOccured) {
        return 1;
      }
      if (a.TimeOccured > b.TimeOccured) {
        return -1;
      }
    }
    return 0;
  }

  mergedEvents?.sort(compare);
  newOrderObj.Events = mergedEvents;

  return newOrderObj;
};

export const base64ToArrayBuffer = (base64: string) => {
  const binaryString = window.atob(base64); // Comment this if not using base64
  const bytes = new Uint8Array(binaryString.length);
  return bytes.map((byte, i) => binaryString.charCodeAt(i));
};

export const createAndDownloadBlobFile = (body: string, filename: string) => {
  const blob = new Blob([body], { type: 'application/pdf' });
  // @ts-ignore
  // no 'name' property from Blobs
  blob.name = filename;
  return URL.createObjectURL(blob);
};

/**
 * shifts targeted object property to position 0
 * @param array array of phone number objects
 * @param target object property
 * @param value property value
 */
export const moveToStart = (
  array?: IPhoneNumber[],
  target?: string,
  value?: string
): IPhoneNumber[] => {
  if (array && array.length) {
    for (let i = 0; i < array.length; i++) {
      if (array[i][target as keyof IPhoneNumber] === value) {
        const a = array.splice(i, 1);
        array.unshift(a[0]);
        break;
      }
    }
  } else {
    array = [];
  }
  return array;
};

export const getProperty = <T, K extends keyof T>(obj: T, key: K): T[K] => {
  return obj[key];
};

export const updatedSearchParams = (
  l: number,
  statuses?: string,
  dateToCompare?: Date,
  dateIsAfter?: boolean,
  searchTermSelected?: string,
  selectedBranch?: string,
  page?: number,
  paginationToken?: string
) => {
  // Saving previous query to compare to next call to make sure persisted pagination token is only used for the same call.
  const toSaveQuery: Record<string, any> = {
    ...(statuses && { Statuses: statuses }),
    ...(dateToCompare && {
      DateToCompareCommissionExpirationTo: dateToCompare.toDateString(),
    }),
    ...(dateIsAfter && { CommissionExpirationDateIsAfter: dateIsAfter }),
    ...(searchTermSelected && { SearchField: searchTermSelected }),
    ...(selectedBranch && { Branch: selectedBranch }),
    Limit: l,
  };

  const savedQuery = createURLParams(toSaveQuery);
  const previousQuery = sessionStorage.getItem('previousQuery');
  if (savedQuery.query != previousQuery) {
    paginationToken = undefined;
  }

  const params: Record<string, any> = {
    ...(statuses && { Statuses: statuses }),
    ...(dateToCompare && {
      DateToCompareCommissionExpirationTo: dateToCompare.toISOString(),
    }),
    ...(dateIsAfter && { CommissionExpirationDateIsAfter: dateIsAfter }),
    ...(searchTermSelected && { SearchField: searchTermSelected }),
    ...(selectedBranch && { Branch: selectedBranch }),
    ...(paginationToken && { PaginationToken: paginationToken }),
    Limit: l,
    Page: page ? page : 0,
  };

  sessionStorage.setItem('previousQuery', savedQuery.query);
  return createURLParams(params);
};

export const createURLParams = (values: Record<string, any>) => {
  let params = '';
  Object.keys(values).forEach((key, i) => {
    if (i == 0) {
      const limit = values[key];
      params = params.concat('?', key, '=', limit.toString());
    } else {
      params = params.concat('&', key, '=', values[key].toString());
    }
  });
  if (values.page !== undefined) {
    params = params.concat('&Page=', values.page);
  }
  if (values.limit !== undefined) {
    params = params.concat('&Limit=', values.limit);
  }

  return { query: params };
};

export const searchFilteredNotaries = (
  limit: number,
  statuses: string,
  dateToCompare: Date,
  dateIsAfter: boolean,
  selectedBranch: string,
  inputVal: string,
  page?: number,
  paginationToken?: string
): ISearchParams | undefined => {
  // When a branch has value, can only search by Active status.
  if (selectedBranch && statuses) {
    statuses = 'Active';
  }
  let searchParams: { query: string } = { query: '' };
  if (limit <= 50) {
    if (selectedBranch) {
      const branch = selectedBranch.split(' ').join('%20');
      searchParams = updatedSearchParams(
        limit,
        statuses,
        dateToCompare,
        dateIsAfter,
        inputVal,
        branch,
        page ? page : 0,
        paginationToken
      );
    } else {
      searchParams = updatedSearchParams(
        limit,
        statuses,
        dateToCompare,
        dateIsAfter,
        inputVal,
        undefined,
        page ? page : 0,
        paginationToken
      );
    }
    return searchParams;
  }
};

export const isFANotary = (email: string | undefined) => {
  if (email) return email.includes('firstam.com');
  return false;
};

export const diff = function (obj1: any, obj2: any) {
  // Make sure an object to compare is provided
  if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
    return obj1;
  }

  const diffs: any = {};
  let key = '';

  /**
   * Check if two arrays are equal
   * @param  {Array}   arr1 The first array
   * @param  {Array}   arr2 The second array
   * @return {Boolean}      If true, both arrays are equal
   */
  const arraysMatch = function (arr1: any, arr2: any) {
    // Check if the arrays are the same length
    if (arr1.length !== arr2.length) return false;

    // Check if all items exist and are in the same order
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }

    // Otherwise, return true
    return true;
  };

  /**
   * Compare two items and push non-matches to object
   * @param  {*}      item1 The first item
   * @param  {*}      item2 The second item
   * @param  {String} key   The key in our object
   */
  const compare = function (item1: any, item2: any, key: any) {
    // Get the object type
    const type1 = Object.prototype.toString.call(item1);
    const type2 = Object.prototype.toString.call(item2);

    // If type2 is undefined it has been removed
    if (type2 === '[object Undefined]') {
      diffs[key] = null;
      return;
    }

    // If items are different types
    if (type1 !== type2) {
      diffs[key] = item2;
      return;
    }

    // If an object, compare recursively
    if (type1 === '[object Object]') {
      const objDiff = diff(item1, item2);
      if (Object.keys(objDiff).length > 0) {
        diffs[key] = objDiff;
      }
      return;
    }

    // If an array, compare
    if (type1 === '[object Array]') {
      if (!arraysMatch(item1, item2)) {
        diffs[key] = item2;
      }
      return;
    }

    // Else if it's a function, convert to a string and compare
    // Otherwise, just compare
    if (type1 === '[object Function]') {
      if (item1.toString() !== item2.toString()) {
        diffs[key] = item2;
      }
    } else {
      if (item1 !== item2) {
        diffs[key] = item2;
      }
    }
  };

  for (key in obj1) {
    compare(obj1[key], obj2[key], key);
  }

  for (key in obj2) {
    if (!obj1[key] && obj1[key] !== obj2[key]) {
      diffs[key] = obj2[key];
    }
  }

  // Return the object of differences
  return diffs;
};

export const getItemColor = (
  itemCompleted: boolean,
  itemDisabled?: boolean,
  clickable?: boolean
) => {
  if (itemCompleted && !itemDisabled) {
    return successGreen;
  } else if (!itemCompleted && !itemDisabled && clickable) {
    return primaryBlue;
  } else if (!clickable) {
    return black;
  } else {
    return black;
  }
};

export const isOrderInactive = (orderStatus: OrderStatus) => {
  const status = orderStatus ? orderStatus.toLowerCase() : null;
  return !!(
    status &&
    (status.includes('archived') || status.includes('canceled'))
  );
};

export const getUpdatedSignerEvents = (
  partyId: number | string,
  order: any
) => {
  const signer = order?.OrderDetails?.Signers?.find(
    (e: any) => e?.Id == partyId
  );
  const authEvent = {
    Name: 'Authenticate, Unlocked',
    DocLink: null,
    TimeOccured: new Date().toISOString(),
  };

  const signerEvents = signer.SolexSigner.SignerEvents.slice();
  signerEvents.push(authEvent);

  return signerEvents;
};

export const setUpdatedSignerEvents = (
  partyId: number | string,
  authenticatedEvents: any,
  order: any
) => {
  return order?.OrderDetails.Signers.slice().map((e: any) => {
    if (e.Id == partyId) {
      return Object.assign({}, e, {
        SolexSigner: { ...e.SolexSigner, SignerEvents: authenticatedEvents },
      });
    } else {
      return e;
    }
  });
};

export const getTextColor = (condition: boolean) =>
  condition ? 'white' : 'primary.main';

export const getValidNotaries = (
  allNotaries: IIPENNotaryProfile[],
  DateToCompare: Date
) => {
  const listOfValidNotaries: IIPENNotaryProfile[] = [];
  if (allNotaries) {
    allNotaries.forEach((notary) => {
      if (notary.Status === Status.Active || notary.Status === Status.Pending) {
        if (notary.CommissionExpirationDate != null) {
          const expirationDate = new Date(notary.CommissionExpirationDate);
          if (expirationDate > DateToCompare) {
            listOfValidNotaries.push(notary);
          }
        } else {
          listOfValidNotaries.push(notary);
        }
      }
    });
  }

  return listOfValidNotaries;
};

export const getPartyName = (party: ISigner): string => {
  const name: IPartyName = {
    FirstName: party?.FirstName,
    LastName: party?.LastName,
    MiddleName: party?.MiddleName,
    Suffix: party?.Suffix,
  };
  return formatPartyName(name, 'signer');
};

export const isOrderTypeIPEN = (data: any) => {
  return data?.OrderType?.Type === 'IPEN';
};

/**
 * Formats a party's name based on their role in the signing process.
 * For signing parties, it includes the first name, middle initial (if provided), last name, and suffix (if provided).
 * For non-signing parties, it includes only the first and last names.
 *
 * @param {IPartyName} name An object containing the first name, last name, optional middle name, and optional suffix of the party.
 * @param {'signer' | 'non-signer'} format the format of the name based on the party's role ('signer' or 'non-signer').
 * @returns {string} The formatted name.
 */
export const formatPartyName = (
  name: IPartyName,
  format: 'signer' | 'non-signer'
): string => {
  let formattedName = '';
  if (name.FirstName) {
    formattedName += name.FirstName;
  }
  if (format === 'signer' && name.MiddleName) {
    const middleInitial = ` ${name.MiddleName.charAt(0)}.`;
    formattedName += middleInitial;
  }
  if (name.LastName) {
    if (formattedName.length > 0) formattedName += ' ';
    formattedName += name.LastName;
  }
  if (format === 'signer' && name.Suffix) {
    formattedName += `, ${name.Suffix}`;
  }
  return formattedName;
};

/**
 * Formats error messages for user-friendly display.
 * This utility function is designed to identify specific patterns in error messages and convert them
 * into more understandable, user-friendly text. It uses substring matching to identify relevant errors.
 * If new cases of error messages need to be formatted, additional conditions can be added.
 *
 * @param {string} errorMessage - The error message received from the system.
 * @returns {string} - The formatted error message or the original message if no specific formatting is applied.
 */
export const solexErrorFormatter = (errorMessage: string): string => {
  if (
    errorMessage.includes(
      'The SealImage provided must be a base64 formatted PNG'
    )
  ) {
    return 'Unable to save. Notary Seal image is an incorrect format. Please ensure the seal is .png and try again.';
  }
  return errorMessage;
};

/**
 * Validates if a Base64 string is a PNG image.
 *
 * This function checks the Base64 encoded string of the file to ensure it has the correct PNG signature.
 * The PNG signature is a unique sequence at the beginning of the file that identifies it as a PNG image.
 *
 * @param {string} base64String The Base64 encoded string of the file.
 * @returns {boolean} True if the Base64 string represents a valid PNG image; otherwise, false.
 */
export const isValidPngBase64 = (base64String: string) => {
  const base64Pattern = /^data:image\/png;base64,/;
  const rawBase64 = base64String.replace(base64Pattern, '');
  const pngBase64Signature = 'iVBORw0KGgo';
  return rawBase64.startsWith(pngBase64Signature);
};

/**
 * Calculates and returns the current window's dimensions.
 * Provides an easy way to access the current window width and height.
 *
 * @returns {Object} An object containing the current window's width and height.
 */
export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};
